import * as Api from '@ViewModels';
import { useCallback } from 'react';
import type { useAutoSave } from './useAutoSave';

type UseAutoSaveReturn = ReturnType<typeof useAutoSave>;

interface UseTextingStepSaveOptions {
	textingStep: Api.ITextingAutomationStep | null;
	stepId: string | undefined;
	templateId: string | undefined;
	skipStepContactFilter: Api.IContactsFilterRequest | null | undefined;
	autoSave: UseAutoSaveReturn;
}

interface UseTextingStepSaveReturn {
	save: (params: {
		newContactFilter?: Api.IContactsFilterRequest | null;
		newSendFrom?: Api.ISendFromOptions | null;
	}) => Promise<void>;
	onSaveStep: (
		stepToSave?: Partial<Api.ITextingAutomationStep>,
		_newSchedule?: Api.IAutomationStepSchedule
	) => Promise<void>;
	saveSenderOptions: (option: Api.ISendFromOptions, sendToSender: boolean) => void;
}

/**
 * Custom hook for managing texting step save operations.
 * Handles save logic for step content, contact filters, and sender options.
 */
export function useTextingStepSave({
	textingStep,
	stepId,
	templateId,
	skipStepContactFilter,
	autoSave,
}: UseTextingStepSaveOptions): UseTextingStepSaveReturn {
	const save = useCallback(
		(params: { newContactFilter?: Api.IContactsFilterRequest | null; newSendFrom?: Api.ISendFromOptions | null }) => {
			const { newContactFilter = skipStepContactFilter, newSendFrom = null } = params;

			if (!textingStep || !stepId || !templateId) {
				return Promise.resolve();
			}

			const stepModel: Api.ITextingAutomationStep = {
				...textingStep,
				contactFilter: newContactFilter,
			};

			if (newSendFrom !== null) {
				stepModel.sendFromOptions = newSendFrom;
			}

			return autoSave.saveStep(stepId, Api.AutomationStepType.Texting, stepModel);
		},
		[textingStep, skipStepContactFilter, stepId, templateId, autoSave]
	);

	const onSaveStep = useCallback(
		(stepToSave?: Partial<Api.ITextingAutomationStep>, _newSchedule?: Api.IAutomationStepSchedule) => {
			if (!textingStep || !stepId || !templateId) {
				return Promise.resolve();
			}

			const stepModel: Api.ITextingAutomationStep = {
				...textingStep,
				...(stepToSave || {}),
			};

			return autoSave.saveStep(stepId, Api.AutomationStepType.Texting, stepModel);
		},
		[textingStep, stepId, templateId, autoSave]
	);

	const saveSenderOptions = useCallback(
		(option: Api.ISendFromOptions, _sendToSender: boolean) => {
			save({ newSendFrom: option });
		},
		[save]
	);

	return {
		save,
		onSaveStep,
		saveSenderOptions,
	};
}
